// Corporate Theme Configuration - Dual Theme System (Dark/Light)
//
// Since 2026-09-24 both modes share ONE colour map of `var(--odin-*)` strings
// that ClaudeVars resolves on <html>, so darkTheme and lightTheme differ only
// in `mode`. Since 2026-09-25 the values behind the variables are the CSG brand
// palette (src/theme/claudeTokens.js). Typography,
// spacing, radii, transitions, z-index, breakpoints and layout are unchanged.
import { SCALE } from './claudeTokens';

// Base theme values shared between dark and light modes
const baseTheme = {
  // Typography
  typography: {
    fontFamily: {
      heading: SCALE.fontSans, // Gabarito: h4+, walk-round, recommendations
      display: SCALE.fontDisplay, // Gabarito Bold: h1–h3, antd titles, stat values, the agent CardH2
      primary: SCALE.fontSans,
      secondary: SCALE.fontMono,
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      black: 900,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },

  // Border radius
  borderRadius: {
    sm: '4px',
    md: '6px',
    lg: '12px',
    card: '14px',
    xl: '18px',
    full: '50%',
  },

  // Transitions
  transitions: {
    fast: '0.15s ease',
    normal: '0.3s ease',
    slow: '0.5s ease',
    bounce: '0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Z-index layers
  zIndex: {
    background: -1,
    normal: 1,
    dropdown: 10,
    sticky: 20,
    overlay: 30,
    modal: 40,
    popover: 50,
    tooltip: 60,
    sidebar: 100,
  },

  // Breakpoints
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // Layout dimensions
  layout: {
    sidebarWidth: '250px',
    sidebarCollapsedWidth: '70px',
    headerHeight: '70px',
    contentPadding: '1.5rem',
  },
};

// The accent differs per mode (Oxford Blue fill in light, Celestial in dark), so
// it is a variable like everything else; tints go through color-mix().
const ACCENT = 'var(--odin-accent)';
const ACCENT_HOVER = 'var(--odin-accent-hover)';
const ACCENT_TINT = 'color-mix(in srgb, var(--odin-accent) 14%, transparent)';
// A neutral hover that darkens in light mode and lightens in dark mode (surface-hover
// on surface is only ~1.06:1 in light, too faint for nav rows and header buttons).
const NEUTRAL_HOVER = 'color-mix(in srgb, var(--odin-text-primary) 6%, transparent)';
const ACCENT_TINT_STRONG = 'color-mix(in srgb, var(--odin-accent) 15%, transparent)';

// One colour map for both modes — every value resolves through the --odin-*
// variables (src/theme/claudeTokens.js). The mode switch is html[data-theme].
const claudeColors = {
  // Background colors
  primary: 'var(--odin-bg)',
  secondary: 'var(--odin-surface)',
  tertiary: 'var(--odin-surface-hover)',

  // Accent: the primary fill (Oxford Blue light / Celestial dark) and the thin Celestial highlight
  accent: ACCENT,
  accentSecondary: ACCENT_HOVER,
  accentHover: ACCENT_HOVER,
  highlight: 'var(--odin-highlight)',

  // Text colors
  text: 'var(--odin-text-primary)',
  textSecondary: 'var(--odin-text-secondary)',
  textMuted: 'var(--odin-text-tertiary)',
  ink: 'var(--odin-ink)', // display ink: Oxford Blue headings and stat values
  textAccent: 'var(--odin-accent-text)', // the accent read as text: Lapis Lazuli, the AA stop

  // UI colors
  surface: 'var(--odin-surface-elevated)',
  surfaceHover: 'var(--odin-surface-hover)',
  surfaceActive: ACCENT_TINT,
  chrome: 'var(--odin-chrome)', // header bar, pills, light controls
  border: 'var(--odin-border)',
  borderStrong: 'var(--odin-border-strong)',
  borderHover: 'var(--odin-border-strong)',
  borderActive: ACCENT,
  hover: NEUTRAL_HOVER,

  // Former glassmorphism colors — now flat elevated surfaces
  glass: 'var(--odin-surface-elevated)',
  glassBorder: 'var(--odin-border)',
  glassHover: 'var(--odin-surface-elevated)',

  // Sidebar specific colors
  sidebarBg: 'var(--odin-surface)',
  sidebarHover: NEUTRAL_HOVER,
  sidebarActive: ACCENT_TINT_STRONG,
  // Three-level tree: item secondary < hover primary < active ink on the tint pill
  sidebarText: 'var(--odin-text-secondary)',
  sidebarTextActive: 'var(--odin-ink)',

  // Status colors (semantic aliases of the status text colours; info is navy-slate)
  success: 'var(--odin-success)',
  warning: 'var(--odin-warning)',
  error: 'var(--odin-error)',
  info: 'var(--odin-info)',
  infoBg: 'var(--odin-info-bg)',
  infoBorder: 'var(--odin-info-border)',

  // Card colors
  cardBg: 'var(--odin-surface-elevated)',
  cardBorder: 'var(--odin-border)',
  cardHover: 'var(--odin-surface-elevated)',
};

// Gradients are flat now: the palette has no gradients.
const claudeGradients = {
  primary: 'var(--odin-bg)',
  accent: ACCENT,
  surface: 'var(--odin-surface-elevated)',
  sidebar: 'var(--odin-surface)',
  card: 'var(--odin-surface-elevated)',
  button: ACCENT,
  userSection: NEUTRAL_HOVER,
};

// Two-layer shadows from the palette (per mode via the variables); the cyan glows are gone.
const claudeShadows = {
  sm: 'var(--odin-shadow-pill)',
  md: 'var(--odin-shadow-card)',
  lg: 'var(--odin-shadow-card-hover)',
  chrome: 'var(--odin-shadow-chrome)',
  glow: 'none',
  glowIntense: 'none',
  accent: 'none',
  inset: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
};

// Dark Theme
export const darkTheme = {
  ...baseTheme,
  mode: 'dark',
  colors: claudeColors,
  gradients: claudeGradients,
  shadows: claudeShadows,
};

// Light Theme (default since 2026-09-24)
export const lightTheme = {
  ...baseTheme,
  mode: 'light',
  colors: claudeColors,
  gradients: claudeGradients,
  shadows: claudeShadows,
};

// Add media queries to both themes
const addMediaQueries = (theme) => {
  theme.mediaQuery = {
    sm: `@media (min-width: ${theme.breakpoints.sm})`,
    md: `@media (min-width: ${theme.breakpoints.md})`,
    lg: `@media (min-width: ${theme.breakpoints.lg})`,
    xl: `@media (min-width: ${theme.breakpoints.xl})`,
    '2xl': `@media (min-width: ${theme.breakpoints['2xl']})`,
    maxSm: `@media (max-width: ${theme.breakpoints.sm})`,
    maxMd: `@media (max-width: ${theme.breakpoints.md})`,
    maxLg: `@media (max-width: ${theme.breakpoints.lg})`,
  };
  return theme;
};

addMediaQueries(darkTheme);
addMediaQueries(lightTheme);

// CSS-in-JS helper functions (use with caution - prefer theme context)
export const getColor = (path, theme = darkTheme) => {
  const keys = path.split('.');
  let value = theme.colors;
  for (const key of keys) {
    value = value[key];
  }
  return value;
};

export const getSpacing = (size, theme = darkTheme) => theme.spacing[size];
export const getRadius = (size, theme = darkTheme) => theme.borderRadius[size];
export const getShadow = (type, theme = darkTheme) => theme.shadows[type];
export const getGradient = (type, theme = darkTheme) => theme.gradients[type];
export const getTransition = (type, theme = darkTheme) => theme.transitions[type];

// Media query helpers
export const mediaQuery = darkTheme.mediaQuery;

// Default export for backward compatibility
export const corporateTheme = lightTheme;
export default corporateTheme;
